import { useState, useCallback, useEffect, useContext } from 'react';

import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Form as FormAntd, message, Row, Spin, Table, Typography } from 'antd';

import { ActionButton } from '@hdi-seguros/web-components.action-button';
import {
  ColSize,
  ContentSection,
  Form,
  InputForm,
  RadioForm,
  RowSize,
  SelectForm,
} from '../../../../../../../../../../../../../shared/components';
import { Modal } from '@hdi-seguros/web-components.modal';

import { GenericDataFormContext } from '../../../../../../../../../shared/contexts';
import {
  StepFourFormContext,
  TableBeneficiaryItem,
} from '../../../../../../contexts/StepFourForm';

import { ValidateForm } from '../../../../../../../../../../../../../shared/utils';

import { COLORS } from '../../../../../../../../../../../../../config/themes/colors';

const OPTION_YES_OR_NOT: {
  label: string;
  value: boolean;
}[] = [
  {
    label: 'SI',
    value: true,
  },
  {
    label: 'NO',
    value: false,
  },
];

export const BeneficiariesForm = () => {
  const validateForm = new ValidateForm();

  const { documentTypeList } = useContext(GenericDataFormContext);
  const { beneficiaries, setBeneficiaries } = useContext(StepFourFormContext);

  const [beneficiariesForm] = FormAntd.useForm();

  const [showBeneficiaryModal, setShowBeneficiaryModal] =
    useState<boolean>(false);
  const [isLoadingSelectList, setIsLoadingSelectList] = useState<boolean>(true);
  const onDeleteBeneficiary = (sequence: string) => {
    onDeleteBeneficiaryOnTable(sequence);
  };

  const onDeleteBeneficiaryOnTable = (key: string) => {
    const filteredBeneficiaries = beneficiaries.filter((el) => el.key !== key);
    setBeneficiaries(filteredBeneficiaries);
  };

  const addNewBeneficiaryOnTable = useCallback(() => {
    const formFields = beneficiariesForm.getFieldsValue();

    const documentTypeDescription: string = documentTypeList?.find(
      (el) => el.value === formFields.documentType
    )?.label!;

    const hasNoRepetitionClauseDescription: string =
      formFields.hasNoRepetitionClause ? 'Si' : 'No';

    const newBeneficiary: TableBeneficiaryItem = {
      key: String(beneficiaries.length + 1),
      documentTypeDescription,
      hasNoRepetitionClauseDescription,
      ...formFields,
    };

    setBeneficiaries((prevValues) => [...prevValues, newBeneficiary]);
    setShowBeneficiaryModal(false);
    beneficiariesForm.resetFields();
  }, [setBeneficiaries, setShowBeneficiaryModal, beneficiaries]);

  const onAddNewBeneficiary = useCallback(async () => {
    try {
      if (beneficiaries.length === 99) {
        message.error('Se puede agregar como máximo 99 beneficiarios.');
      }

      await beneficiariesForm.validateFields();

      addNewBeneficiaryOnTable();
    } catch (error) {
    }
  }, [beneficiaries, addNewBeneficiaryOnTable]);

  const onCancelBeneficiaryModal = () => {
    beneficiariesForm.resetFields();
    setShowBeneficiaryModal(false);
  };

  const MOCK_TABLE_COLUMNS_HEADER = [
    {
      title: 'Apellido y Nombre / Razón social',
      dataIndex: 'fullName',
      width: '20%',
    },
    {
      title: 'Tipo de Documento',
      dataIndex: 'documentTypeDescription',
      width: '20%',
    },
    {
      title: 'Nro. de Documento',
      dataIndex: 'documentNumber',
      width: '20%',
    },
    {
      title: 'CUIL/CUIT',
      dataIndex: 'cuil',
      width: '20%',
    },
    {
      title: 'Cláu. No Repetición',
      dataIndex: 'hasNoRepetitionClauseDescription',
      width: '10%',
    },
    {
      title: 'Acción',
      dataIndex: 'action',
      className: 'action-column',
      render: (_: any, data: TableBeneficiaryItem) => (
        <ActionButton
          color='danger'
          icon={<DeleteOutlined />}
          label=''
          onClick={() => onDeleteBeneficiary(data.key)}
          type='secondary'
        />
      ),
    },
  ];

  useEffect(() => {
    if (documentTypeList?.length) setIsLoadingSelectList(false);
  }, [documentTypeList]);

  return (
    <ContentSection level='h3' title='Beneficiarios'>
      <RowSize rowProps={{
          gutter: 0
        }}>
        <RowSize rowProps={{ align: 'middle', justify: 'end' }}>
          <ActionButton
            className='btn-accent-outline-barbuss'
            icon={<PlusOutlined />}
            label='Agregar Beneficiario'
            onClick={() => setShowBeneficiaryModal(true)}
            type='secondary'
          />
        </RowSize>

        <Table
          bordered
          columns={MOCK_TABLE_COLUMNS_HEADER}
          dataSource={beneficiaries}
          pagination={false}
          rowClassName={() => 'editable-row'}
          className='beneficiary-table'
        />
      </RowSize>

      <Modal
        centered
        confirmText='Guardar'
        footer
        onCancel={onCancelBeneficiaryModal}
        onOk={onAddNewBeneficiary}
        open={showBeneficiaryModal}
        width={876}
      >
        <Form
          formProps={{
            form: beneficiariesForm,
            name: 'beneficiaries-form',
          }}
        >
          <Spin spinning={false}>
            <Row>
              <Typography.Title
                level={4}
                style={{
                  margin: 0,
                  marginBottom: 10,
                  color: COLORS.secondary[100],
                }}
              >
                Nuevo Beneficiario
              </Typography.Title>
            </Row>

            <RowSize>
              <ColSize size={25}>
                <InputForm
                  formProps={{
                    label: 'Apellido y Nombre / Razón Social:',
                    name: 'fullName',
                    rules: validateForm.generateRules({
                      label: 'apellido y nombre / razón social',
                      rules: ['required', 'fullName'],
                    }),
                  }}
                  inputProps={{
                    placeholder: 'Apellido y nombre / Razón social',
                  }}
                />
              </ColSize>

              <ColSize size={25}>
                <SelectForm
                  formProps={{
                    label: 'Tipo de Documento:',
                    name: 'documentType',
                    rules: validateForm.generateRules({
                      label: 'tipo de documento',
                      rules: ['required'],
                    }),
                  }}
                  selectProps={{
                    disabled: !documentTypeList?.length,
                    loading: isLoadingSelectList,
                    options: documentTypeList,
                  }}
                />
              </ColSize>

              <ColSize size={25}>
                <InputForm
                  formProps={{
                    label: 'Número de Documento:',
                    name: 'documentNumber',
                    rules: validateForm.generateRules({
                      label: 'número de documento',
                      rules: ['required', 'number'],
                    }),
                  }}
                  inputProps={{ placeholder: '32478963' }}
                />
              </ColSize>

              <ColSize size={25}>
                <InputForm
                  formProps={{
                    label: 'CUIL / CUIT:',
                    name: 'cuil',
                    rules: validateForm.generateRules({
                      label: 'cuil / cuit',
                      rules: ['required', 'minLength:11', 'maxLength:11', 'cuil'],
                    }),
                  }}
                  inputProps={{ placeholder: '23324789639' }}
                />
              </ColSize>

              <ColSize size={25}>
                <RadioForm
                  formProps={{
                    initialValue: false,
                    label: 'Cláusula de No Repetición:',
                    name: 'hasNoRepetitionClause',
                  }}
                  options={OPTION_YES_OR_NOT}
                />
              </ColSize>
            </RowSize>
          </Spin>
        </Form>
      </Modal>
    </ContentSection>
  );
};